import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorText } from '../lib/api';
import { hasRole, isStaff, useMe } from '../lib/session';
import { fmtDate, fmtR, fmtTime, signClass } from '../lib/format';
import { CHECKLIST_BAND_LABEL, type StrategyStats } from '@vtf/shared';
import { audienceText, LEVEL_LABEL, type StrategyDetail } from '../lib/library';
import { Bar, Button, Card, CardHeader, cx, ErrorText, PageHeader, Pill } from '../components/ui';
import { FinishedCard, Prose, RequiredLearningAdmin, SignedImage, StatusActions, StatusPill, TypeBadge } from '../components/library/parts';

/** INT-LIB-03: a strategy, its current or an earlier version, the pre-trade checklist and the version history. */

function Section({ title, text }: { title: string; text: string | null }) {
  if (!text) return null;
  return (
    <Card className="flex flex-col gap-2 p-4">
      <h2 className="m-0 text-[15px] font-semibold">{title}</h2>
      <Prose text={text} />
    </Card>
  );
}

/**
 * INT-LIB-06: how closed floor trades tagged with this strategy performed (all versions). Figures stay hidden until
 * STRATEGY_STATS_MIN_TRADES trades exist, so a handful of lucky or unlucky trades can't define the strategy.
 */
function FloorStats({ code }: { code: string }) {
  const { data: s } = useQuery({ queryKey: ['strategy-stats', code], queryFn: () => api<StrategyStats>(`/library/strategies/${encodeURIComponent(code)}/stats`) });
  if (!s) return null;
  return (
    <Card>
      <CardHeader title="How floor trades did" right={<span className="text-xs text-muted">{s.closedTrades} closed trade{s.closedTrades === 1 ? '' : 's'}</span>} />
      {!s.enough ? (
        <div className="flex flex-col gap-2 px-4 py-3">
          <span className="text-sm text-ink2">
            Results show once {s.minTrades} closed floor trades use this strategy — {s.closedTrades} so far.
          </span>
          <Bar pct={(s.closedTrades / s.minTrades) * 100} />
        </div>
      ) : (
        <>
          <dl className="m-0 grid grid-cols-3 gap-2 px-4 py-3">
            {[
              ['Trades', String(s.closedTrades), ''],
              ['Win rate', s.winRate ? `${s.winRate} %` : '—', ''],
              ['Average R', fmtR(s.avgR), signClass(s.avgR)],
            ].map(([k, v, tone]) => (
              <div key={k} className="flex flex-col gap-0.5">
                <dt className="text-xs text-muted">{k}</dt>
                <dd className={cx('m-0 font-mono text-lg font-semibold', tone)}>{v}</dd>
              </div>
            ))}
          </dl>
          {!!s.bands?.length && (
            <div className="border-t border-line px-4 py-3">
              <h3 className="m-0 mb-2 text-xs font-medium tracking-[0.06em] text-muted uppercase">By checklist on the plan</h3>
              <ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-sm">
                {s.bands.map((b) => (
                  <li key={b.band} className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <span className="text-ink2">{CHECKLIST_BAND_LABEL[b.band]}</span>
                    {b.enough ? (
                      <span className="font-mono">
                        {b.trades} · {b.winRate} % · <span className={signClass(b.avgR)}>{fmtR(b.avgR)}</span>
                      </span>
                    ) : (
                      <span className="text-xs text-muted">
                        {b.trades} trade{b.trades === 1 ? '' : 's'} · too few to show
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      <p className="m-0 border-t border-line px-4 py-2.5 text-xs text-muted">All versions · floor trades only · average R over trades with a stop. Past results are no guide to future results.</p>
    </Card>
  );
}

export function StrategyPage() {
  const { code = '' } = useParams();
  const [search, setSearch] = useSearchParams();
  const version = search.get('version');
  const me = useMe();
  const qc = useQueryClient();
  const nav = useNavigate();
  const editor = hasRole(me.data?.roles, 'MASTER_TRADER', 'ADMIN');
  const staff = isStaff(me.data?.roles);
  const tz = me.data?.timezone ?? 'UTC';
  const { data: d, error } = useQuery({ queryKey: ['strategy', code, version], queryFn: () => api<StrategyDetail>(`/library/strategies/${encodeURIComponent(code)}${version ? `?version=${version}` : ''}`) });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['strategy', code] });
    void qc.invalidateQueries({ queryKey: ['library'] });
    void qc.invalidateQueries({ queryKey: ['today'] });
  };
  const follow = useMutation({ mutationFn: (on: boolean) => api(`/library/strategies/${d!.strategy.id}/follow`, { method: on ? 'POST' : 'DELETE' }), onSuccess: refresh });

  if (error)
    return (
      <>
        <PageHeader title="Strategy" back={<Link to="/library" className="text-[13px]">← Library</Link>} />
        <ErrorText error={errorText(error)} />
      </>
    );
  if (!d) return null;
  const v = d.version;
  const old = v.version !== d.strategy.currentVersion;

  return (
    <>
      <PageHeader
        title={
          <>
            <span className="font-mono text-accent">{d.strategy.code}</span> · {d.strategy.name}
          </>
        }
        sub={`v${v.version}, ${v.version === 1 ? 'published' : 'changed'} by ${v.publishedBy.name} on ${fmtDate(v.publishedAt, tz)}`}
        back={<Link to="/library" className="text-[13px]">← Library</Link>}
        right={
          <>
            <StatusPill status={d.item.status} />
            <Button onClick={() => follow.mutate(!d.strategy.following)} disabled={follow.isPending} aria-pressed={d.strategy.following}>
              {d.strategy.following ? '✓ Following' : 'Follow updates'}
            </Button>
            {editor && (
              <>
                <Button variant="primary" onClick={() => nav(`/library/strategies/${encodeURIComponent(d.strategy.code)}/edit`)}>
                  {d.item.publishedAt ? 'New version' : 'Edit draft'}
                </Button>
                <StatusActions item={d.item} onDone={refresh} />
              </>
            )}
          </>
        }
      />
      {old && (
        <Card tone="warn" className="flex flex-wrap items-center justify-between gap-2 p-3.5">
          <span>
            You're reading <strong>v{v.version}</strong>. The current version is v{d.strategy.currentVersion}.
          </span>
          <Button onClick={() => setSearch({})}>Show the current version</Button>
        </Card>
      )}
      <div className="flex flex-wrap items-start gap-4">
        <section className="flex min-w-0 flex-[999_1_560px] flex-col gap-3.5">
          <Card className="flex flex-col gap-3 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <TypeBadge type="STRATEGY" />
              {d.item.level && <span className="text-xs text-muted">{LEVEL_LABEL[d.item.level]}</span>}
              {v.markets.map((m) => (
                <Pill key={m} tone="accent">
                  {m}
                </Pill>
              ))}
              {v.timeframes.map((t) => (
                <Pill key={t}>{t}</Pill>
              ))}
            </div>
            {d.item.description && <p className="m-0 text-base leading-relaxed text-ink">{d.item.description}</p>}
            <h2 className="m-0 mt-1 text-[15px] font-semibold">Concept</h2>
            <Prose text={v.concept} />
          </Card>
          <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2">
            <Section title="Setup conditions" text={v.setupConditions} />
            <Section title="Entry trigger" text={v.entryTrigger} />
            <Section title="Stop-loss rule" text={v.stopLossRule} />
            <Section title="Take-profit rule" text={v.takeProfitRule} />
            <Section title="Risk rule" text={v.riskRule} />
            <Section title="Don't trade when…" text={v.noTradeFilters} />
          </div>
          {v.examples.length > 0 && (
            <Card className="flex flex-col gap-3 p-4">
              <h2 className="m-0 text-[15px] font-semibold">Example charts</h2>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {v.examples.map((e, n) => (
                  <figure key={n} className="m-0 flex flex-col gap-1.5">
                    {e.file ? (
                      <SignedImage itemId={d.item.id} file={e.file} alt={e.caption || `Example ${n + 1}`} />
                    ) : (
                      e.url && (
                        <a href={e.url} target="_blank" rel="noopener noreferrer" className="rounded-lg border border-line2 bg-raised p-3 font-semibold break-all">
                          {e.caption || e.url} ↗
                        </a>
                      )
                    )}
                    {e.file && e.caption && <figcaption className="text-sm text-sub">{e.caption}</figcaption>}
                  </figure>
                ))}
              </div>
            </Card>
          )}
          {d.related.length > 0 && (
            <Card className="flex flex-col gap-2 p-4">
              <h2 className="m-0 text-[15px] font-semibold">Linked videos and documents</h2>
              {d.related.map((r) => (
                <Link key={r.id} to={r.link} className="flex items-center gap-2 rounded-lg bg-raised px-3 py-2.5 text-ink no-underline hover:text-accent">
                  <TypeBadge type={r.type} /> <span className="font-semibold">{r.title}</span>
                </Link>
              ))}
            </Card>
          )}
          {staff && <RequiredLearningAdmin item={d.item} canAssign={editor} />}
        </section>

        <aside className="flex min-w-0 flex-[1_1_320px] flex-col gap-3.5">
          <Card tone="up">
            <CardHeader title="Pre-trade checklist" right={<span className="text-xs text-muted">{v.checklist.length} items</span>} />
            <ol className="m-0 flex list-none flex-col gap-0 p-0">
              {v.checklist.map((c, n) => (
                <li key={c.id} className="flex items-start gap-3 border-t border-[#1a1f27] px-4 py-2.5 first:border-t-0">
                  <span className="mt-0.5 font-mono text-xs text-muted">{n + 1}</span>
                  <span className="flex-1">{c.text}</span>
                  {c.required && <Pill tone="warn">Must-have</Pill>}
                </li>
              ))}
            </ol>
            <p className="m-0 border-t border-line px-4 py-2.5 text-xs text-muted">Pick this strategy on a new trade plan to tick the checklist; unticked items are shown to the Risk Manager.</p>
          </Card>
          <FloorStats code={d.strategy.code} />
          {(!editor || d.item.me.required) && <FinishedCard item={d.item} onChange={refresh} />}
          <Card>
            <CardHeader title="Versions" right={<span className="text-xs text-muted">select one to read it</span>} />
            <ol className="m-0 flex list-none flex-col p-0">
              {d.versions.map((x) => (
                <li key={x.id} className={cx('flex flex-col gap-0.5 border-t border-[#1a1f27] px-4 py-2.5 first:border-t-0', x.version === v.version && 'bg-[#141a24]')}>
                  <span className="text-sm">
                    <button type="button" aria-current={x.version === v.version ? 'true' : undefined} className="cursor-pointer font-semibold text-accent" onClick={() => setSearch(x.version === d.strategy.currentVersion ? {} : { version: String(x.version) })}>
                      v{x.version}
                    </button>{' '}
                    <span className="text-sub">
                      · {x.publishedBy.name} · {fmtTime(x.publishedAt, tz, true)}
                    </span>
                  </span>
                  <span className="text-sm text-ink2">{x.changelog ?? (x.version === 1 ? 'First version' : '')}</span>
                </li>
              ))}
            </ol>
          </Card>
          {staff && d.item.audience && (
            <Card className="p-4 text-sm text-sub">
              Shown to: <strong className="text-ink">{audienceText(d.item.audience)}</strong>
            </Card>
          )}
          <span className="text-xs text-muted">Education only. General information, not personal financial advice. Trading involves risk.</span>
        </aside>
      </div>
    </>
  );
}
